import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";

// Settings page components
import UserSettings from "./components/UserSettings";
import UserManagement from "./components/UserManagement";
import OrganizationSettings from "./components/OrganizationSettings";
import ReportsAndSettings from "./components/ReportsAndSettings";

const settingsPages = [
  {
    id: "account",
    title: "User Settings",
    icon: "person",
    component: UserSettings,
    description: "Personal account settings and preferences"
  },
  {
    id: "users",
    title: "User Management",
    icon: "people",
    component: UserManagement,
    description: "Manage team members and user permissions",
    badge: "3"
  },
  {
    id: "organization",
    title: "Organization",
    icon: "business",
    component: OrganizationSettings,
    description: "Company information and organization settings"
  },
  {
    id: "reports-settings",
    title: "Reports and Settings",
    icon: "settings_applications",
    component: ReportsAndSettings,
    description: "Report preferences and Sabbath observance settings"
  }
];

function Settings() {
  console.log('Settings page loaded, settingsPages:', settingsPages);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get current page from URL or default to account
  const getCurrentPage = () => {
    const params = new URLSearchParams(location.search);
    return params.get('page') || 'account';
  };
  
  const [activePage, setActivePage] = useState(getCurrentPage());
  
  const handlePageChange = (pageId) => {
    setActivePage(pageId);
    navigate(`/settings?page=${pageId}`, { replace: true });
  };
  
  const currentPageData = settingsPages.find(page => page.id === activePage);
  const CurrentComponent = currentPageData?.component || UserSettings;
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container spacing={3}>
          {/* Settings Navigation */}
          <Grid item xs={12} lg={3}>
            <Card>
              <ArgonBox p={2}>
                <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                  Settings
                </ArgonTypography>
                <List disablePadding>
                  {settingsPages.map((page, index) => (
                    <div key={page.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={activePage === page.id}
                          onClick={() => handlePageChange(page.id)}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(26, 115, 232, 0.08)',
                              '&:hover': {
                                backgroundColor: 'rgba(26, 115, 232, 0.12)',
                              },
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Icon 
                              fontSize="small"
                              color={activePage === page.id ? "primary" : "inherit"}
                            >
                              {page.icon}
                            </Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
                                <ArgonTypography
                                  variant="body2"
                                  fontWeight={activePage === page.id ? "bold" : "regular"}
                                  color={activePage === page.id ? "info" : "inherit"}
                                >
                                  {page.title}
                                </ArgonTypography>
                                {page.badge && (
                                  <Badge 
                                    badgeContent={page.badge} 
                                    color="error"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </ArgonBox>
                            }
                            secondary={
                              <ArgonTypography variant="caption" color="text">
                                {page.description}
                              </ArgonTypography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < settingsPages.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                      )}
                    </div>
                  ))}
                </List>
              </ArgonBox>
            </Card>
          </Grid>

          {/* Settings Content */}
          <Grid item xs={12} lg={9}>
            <Card>
              <ArgonBox>
                <CurrentComponent />
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Settings;